/**
 * MK.3 — Marketplace tab inside the Skill Library window.
 *
 * Renders the catalog of pre-baked legal skills (litigation / corporate /
 * estate). Each row carries an Install button. Already-installed rows
 * show "Installed" with a checkmark.
 *
 * Install routes to `skillMarketplace.installMarketplaceSkill` (live
 * Convex; demo branching retired in F-FIRST-RUN-LIVE-DATA).
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useResolvedPortalSlug } from '../../lib/tenant/portalSlugFromUrl';
import { testIds } from '../../lib/testIds';

type Category = 'litigation' | 'corporate' | 'estate';
const CATEGORIES: ReadonlyArray<{ id: Category | 'all'; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'litigation', label: 'Litigation' },
  { id: 'corporate', label: 'Corporate / M&A' },
  { id: 'estate', label: 'Estate Planning' },
];

export function MarketplaceTab(): React.ReactElement {
  const clientSlug = useResolvedPortalSlug();

  const [filter, setFilter] = useState<Category | 'all'>('all');
  const [installing, setInstalling] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const catalog = useQuery(
    api.skillMarketplace.listMarketplace,
    filter === 'all' ? {} : { category: filter },
  );
  const installed = useQuery(
    api.skillMarketplace.installedSlugs,
    clientSlug !== null ? { clientSlug } : 'skip',
  );
  const installedSet = useMemo(
    () => new Set((installed ?? []).map((row) => row.marketplaceSlug)),
    [installed],
  );

  const installSkill = useMutation(api.skillMarketplace.installMarketplaceSkill);

  if (clientSlug === null) {
    return (
      <div
        data-testid={testIds.skills.marketplaceRoot}
        className="p-4 text-sm text-black"
      >
        <p className="font-semibold">Skill Marketplace</p>
        <p className="mt-2">
          Open a portal first (e.g. <code className="font-mono">/c/morrison-legal</code>)
          to install marketplace skills into the firm&rsquo;s library.
        </p>
      </div>
    );
  }

  async function handleInstall(slug: string) {
    if (clientSlug === null) return;
    setInstalling(slug);
    setError(null);
    try {
      await installSkill({
        marketplaceSlug: slug,
        clientSlug,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setInstalling(null);
    }
  }

  return (
    <div
      data-testid={testIds.skills.marketplaceRoot}
      className="flex flex-col h-full w-full overflow-hidden"
      style={{ fontFamily: 'var(--xp-font-body)', fontSize: 12 }}
    >
      {/* Category filter chips */}
      <div
        className="flex items-center gap-1 p-2"
        style={{ borderBottom: '1px solid #aca899' }}
      >
        <span className="text-[11px] font-bold mr-2">Category:</span>
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            type="button"
            data-testid={testIds.skills.marketplaceCategoryChip(cat.id)}
            onClick={() => setFilter(cat.id)}
            className="text-[11px] px-2 py-0.5 cursor-pointer select-none"
            style={{
              border: filter === cat.id
                ? '1px solid #003c74'
                : '1px solid #aca899',
              background:
                filter === cat.id
                  ? 'linear-gradient(180deg, #4d8ee8, #1a55b8)'
                  : 'linear-gradient(180deg, #fff, #ecebe5 86%, #d8d0c4)',
              color: filter === cat.id ? '#fff' : '#000',
              textShadow:
                filter === cat.id ? '1px 1px 1px rgba(0,0,0,0.4)' : 'none',
              borderRadius: 3,
            }}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Catalog list */}
      <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-2">
        {catalog === undefined ? (
          <div className="text-[11px] italic text-black">Loading catalog…</div>
        ) : catalog.length === 0 ? (
          <div className="text-[11px] italic text-black">
            No marketplace skills match this filter.
          </div>
        ) : (
          catalog.map((row) => {
            const isInstalled = installedSet.has(row.slug);
            const isBusy = installing === row.slug;
            return (
              <div
                key={row.slug}
                data-testid={testIds.skills.marketplaceRow(row.slug)}
                className="flex items-start gap-3 p-2"
                style={{
                  border: '1px solid #7f9db9',
                  background: '#fff',
                  borderRadius: 3,
                }}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[12px] flex items-center gap-2">
                    <span>{row.name}</span>
                    <span
                      className="text-[10px] px-1.5 py-0 leading-tight"
                      style={{
                        border: '1px solid #aca899',
                        background: '#ece9d8',
                        borderRadius: 2,
                        textTransform: 'capitalize',
                      }}
                    >
                      {row.category}
                    </span>
                  </div>
                  <div className="text-[11px] mt-1 text-black/85">
                    {row.description}
                  </div>
                  <div className="text-[10px] mt-1 text-black/65">
                    by {row.author} · v{row.version}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {isInstalled ? (
                    <span
                      data-testid={testIds.skills.marketplaceInstalledBadge(row.slug)}
                      className="text-[11px] px-2 py-1 font-bold"
                      style={{
                        background: '#d4f5d4',
                        color: '#1a5c1a',
                        border: '1px solid #1a5c1a',
                        borderRadius: 3,
                      }}
                    >
                      ✓ Installed
                    </span>
                  ) : (
                    <button
                      type="button"
                      data-testid={testIds.skills.marketplaceInstallButton(row.slug)}
                      onClick={() => void handleInstall(row.slug)}
                      disabled={isBusy}
                      className="text-[11px] px-3 py-1 cursor-pointer font-bold disabled:opacity-50"
                      style={{
                        background:
                          'linear-gradient(180deg, #fff, #ecebe5 86%, #d8d0c4)',
                        color: '#000',
                        border: '1px solid #003c74',
                        borderRadius: 3,
                      }}
                    >
                      {isBusy ? 'Installing…' : 'Install'}
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
        {error !== null && (
          <div
            data-testid={testIds.skills.marketplaceError}
            className="text-[11px] text-[#7a0a0a] font-bold p-2"
            style={{ background: '#ffeaea', border: '1px solid #7a0a0a' }}
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
